import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { fetchPendingBessResigns, BessResignError } from "../api/bessResign";
import type { BessResignPendingRow } from "../types/bessResignTypes";

type BessResignState = {
  pending: BessResignPendingRow[];
  status: "idle" | "loading" | "error";
  error: string | null;
};

const initialState: BessResignState = {
  pending: [],
  status: "idle",
  error: null,
};

export const fetchPendingBessResignsThunk = createAsyncThunk<
  BessResignPendingRow[],
  { companyId: number; token: string },
  { rejectValue: string }
>("bessResign/fetchPending", async ({ companyId, token }, { rejectWithValue }) => {
  try {
    const { pending } = await fetchPendingBessResigns(companyId, token);
    return pending;
  } catch (err) {
    const message = err instanceof BessResignError ? err.message : "Failed to load pending BESS resigns";
    return rejectWithValue(message);
  }
});

const bessResignSlice = createSlice({
  name: "bessResign",
  initialState,
  reducers: {
    clearBessResign(state) {
      state.pending = [];
      state.status = "idle";
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPendingBessResignsThunk.pending, (state) => {
        state.status = "loading";
        state.error = null;
        state.pending = [];
      })
      .addCase(
        fetchPendingBessResignsThunk.fulfilled,
        (state, action: PayloadAction<BessResignPendingRow[]>) => {
          state.status = "idle";
          state.pending = action.payload;
        },
      )
      .addCase(fetchPendingBessResignsThunk.rejected, (state, action) => {
        state.status = "error";
        state.error = action.payload ?? "Failed to load pending BESS resigns";
      });
  },
});

export const { clearBessResign } = bessResignSlice.actions;

export const selectPendingBessResigns = (state: { bessResign: BessResignState }) => state.bessResign.pending;
export const selectBessResignStatus = (state: { bessResign: BessResignState }) => state.bessResign.status;

export default bessResignSlice.reducer;
